/* eslint-disable */
/**
 * 1. Purpose of this Feature
 * To assist users when editing their queries in the query pad providing basic lintings before running the query
 * 
 * 2. Static Variables (updated: 2019/09)
 * * STATEMENT: categories of statements
 * * KEYWORD: the functional key words used in a query (single word)
 *   	There might be a little overlap between Keyowrd and some of the following variables
 * * KEYWORD_LOGIC: special keyword followed by logical conditions
 * * KEYWORD_PAIRED: special keyowrd that shows in pairs (has to be identified due to the logic of our parser: split)
 * * OPERATOR: operators used in the query (not used yet)
 * * SEPARATOR: characters used to seperate the query text
 * * DATE_TIME: (not used yet)
 * * COMMENT_SIGNAL: charater symbols used as the start of a comment
 * * subqueryChunk: the abstract symbol used to fill in the subquery chunk in the parent query.
 *   	All the subqueries are managed in a bottom up manner. So the most exterior query are dealt at last.
 *   	So we only need to concern with a single layer of query each time (not nest)
 * * TOKEN_TYPES: all types of tokens. (need to be updated according to the need of checkers)
 * 
 * 3. Main Classes (updated: 2019/10/18)
 * * SQLLint
 * * Query
 * * - Line
 * * Subquery
 * * - Token
 * * - Statement Classes (i.e. Select, Use.)
 * * CheckerResult
 * * Check
 * * CheckFactory
 * * All the RULE Classes
 * 
 * 4. Supoorted Command (updated: 2019/09)
 * * SELECT
 * 
 * 5. Currently supported checking rules (updated 09/30/2019)
 * * unmatchedParenthese 
 * * oddCodePoint
 * * missingColumnName
 * * missingTableName
 * * missingColSeparator
 * * missingExpression
 * * missingOrderByEntities
 * * missingGroupByEntities
 * * missingLimitNumber
 * * invalidTableName
 * * invalidColumnName
 * 
 * 6. Issues (updated 11/04/2019)
 * * query functions (they are ignored for now)
 * * `with` statement (they are ignored for now)
 * * previously can only access snowflake tables, not Views
 */


/**
 * ****************************
 * Global Modules
 * ****************************
 */
import _ from 'lodash'
import store from '../store'

/**
 * ****************************
 * Global Static Vars
 * ****************************
 */
const STATEMENT = {
  'Select': 'select',
  'Use': 'use'
}

// This are mostly based on Snowflake documantation, might be differ from Mysql or other databases
export const STATEMENT_UNSUPPORTED = {
  'Delete': 'delete',
  'Drop': 'drop',
  'Undrop': 'undrop',
  'Create': 'create',
  'Insert': 'insert',
  'Alter': 'alter',
  'Truncate': 'truncate',
  'Copy': 'copy',
  'Merge': 'merge',
  'Replace': 'replace',
  'Update': 'update',
  'Put': 'put',
  'Remove': 'remove'
}

export const KEYWORD = {
  'Alter': 'alter',
  'Begin': 'begin',
  'Call': 'call',
  'Create': 'create',
  'Declare': 'declare',
  'Delete': 'delete',
  'Drop': 'drop',
  'Else': 'else',
  'From': 'from',
  'Insert': 'insert',
  'Join': 'join',
  'Leave': 'leave',
  'Limit': 'limit',
  'Replace': 'replace',
  'Return': 'return',
  'Select': 'select',
  'Set': 'set',
  'Show': 'show',
  'Truncate': 'truncate',
  'Update': 'update',
  'Use': 'use',
  'By': 'by',
  'As': 'as',
  'With': 'with',
  'Where':'where',
  'Having': 'having',
  'If':'if',
  'On': 'on',
  'Over': 'over',
  'Cast': '::',
  'DESC': 'desc',
  'ASC': 'asc',
  'Distinct': 'distinct',
  'Case': 'case',
  'Then': 'then',
  'End': 'end',
  'When': 'when',
  'Union': 'union',
  'AllColumn': '*'
}

/** 
 * Combined Keywords
 * Keyword pairs that need special treatment, eg. map for two-elems keyword
 * Lowercase key for easier mapping (select tokenization)
 */
const KEYWORD_PAIRED = {
  'order': 'order by',
  'group': 'group by',
  'inner': 'inner join',
  'left': 'left join',
  'right': 'right join',
  'outter': 'outter join',
  'partition': 'partition by'
}

const PREDEFINED_VAR = {
  'Null': 'null'
}

const SEPARATOR= {
  'Comma': ',',
  'Space': ' ',
  'Newline': '\n',
  'WindowsNewline': '\r\n',
  'LeftPar': '(',
  'RightPar': ')',
  'SingleQuote': '\''
}

const COMMENT_SIGNAL = {
  'CommentDash': '--',
  'CommentMultiLineStart': '/*',
  'CommentMultiLineEnd': '*/'
}

// value is the operator type, either binary, trinocular or negation
const OPERATOR = {
  'is': 'binary',
  'in': 'binary',
  'not': 'negation',
  '<': 'binary',
  '<=': 'binary',
  '>': 'binary',
  '>=': 'binary',
  '<>': 'binary',
  '=': 'binary',
  'between': 'trinocular'
}

const CONCAT = {
  'And': 'and',
  'Or': 'or'
}

const TOKEN_TYPES = {
  'Keyword': 'keyword', //including keyword_paired and keyword_logical
  'PredefinedVar': 'predefined_var',
  'TableName': 'table_name',
  'ColumnName': 'column_name',
  'Option': 'option',
  'Unidentified': '???',
  'Alias': 'alias',
  'Operator': 'operator',
  'Operator_Left': 'operator_left',
  'Operator_Right': 'operator_right',
  'Separator': 'separator',
  'Concat': 'concat',
  'CommentSignal': 'comment_signal',
  'Number': 'number',
  'Quoted_String': 'quoted_string',
  'DateAndTime': 'date_and_time',
  'Subquery_Chunk': 'subquery_chunk',
  'Function_Chunk': 'function_chunk',
  'Function_Name': 'function_name'
}

const CHECK_RULE = [
  'unmatchedParentheses',
  'unmatchedQuotes',
  'missingColumnName',
  'missingTableName',
  'missingAliasName',
  'missingColSeparator',
  'missingExpression',
  'missingOrderByEntities',
  'missingGroupByEntities',
  'missingLimitNumber',
  'missingAliasForSubquery',
  'invalidTableName',
  'invalidColumnName',
  'invalidCondition'
]

const MESSAGE_TYPE = {
  'WARNING': 'warning',
  'ERROR': 'error'
}

const subqueryChunk = 'subquery_chunk' // Symbol to replace the subquery body: ()
const functionChunk = 'function_chunk' // Symbol to replace the functionality body: func()

/**
 * ****************************
 * Global Variables
 * ****************************
 */
let snowflakeTables = {}
let snowflakeTablesTree = {}
const allTablesColumns = { tables: [], columns: []} // All tables and columns thourght the whole query
let subqueryTables = {} // All tables refered in the single subquery, in the format of {table/alias: column/alias list}
let subquerySelectedColumns = {} // All columns refered in the single subquery, in the format of {column: column/alias}, the Values maintains the true refered cols
let subqueryReturnedColumns = {} // All columns selected and returned from child subquery
let subqueryId = 0
let aliasMap = {} // All mappings between the alias and its original table/column names
let assertionState = {} // state for each rule( true for pass | false for fail) for each subquery, used for rule dependency control
let queryTokens = [] // Tokenization list per subquery

/**
 * ****************************
 * Main Lint Check Class
 * The default exported class to trigger and do lint checking
 * The Lint Class regards to the whole codemirror instance (include all raw text and relative error/warnings)
 * ****************************
 */
export class SQLLint {
  /**
   * Constructor of the class, user can feed in the query text and rules to ignore when initiate the instance
   * @param {String} rawText The raw query text extracted from editor
   * @param {Array} omittedErrors 1D array of checker rules to skip
   */
  constructor (rawText, omittedErrors = []) {
    this.rawText = rawText
    this.omittedErrors = omittedErrors
    this.messageMatrix = []
    this.__initData__()
  }

  async __initData__ () {
    if(!store.getters['snowflake/snowflakeTables']) {
      await store.dispatch('snowflake/fetchSnowflakeData')
    }
    snowflakeTables = store.getters['snowflake/snowflakeTables']
    prepareSnowflakeTables(snowflakeTables)
    Object.keys(allTablesColumns).forEach(key => allTablesColumns[key] = [])
    queryTokens = []
  }

  /**
   * Main Lint Check Function
   * Construct an array of Query instances
   * For each Query instance, check for `unmatchedPartheses` rule first
   * If the query pass that, parse and tokenize the query into depper level and check for the rest of the rules
   * Concat the `messageArray` and `messageMatrix` with CheckerResult instances on the fly
   * Return the messageMatrix which should be containing checking results for all queries
   */
  lintCheck () {
    // Extract an array of queries based on separator of ';'
    const queries = getQueryFromLine(this.rawText)
    this.messageMatrix = []
    // For each query in the query list, go through the parsing, tokenizing, checking and emiting stages
    for (const query of queries) {
      const messageArray = []
      // Firstly, need to check if the parentheses are legally paired
      // the query here are original text of the query (not same as conetent from subqueryTokenised)
      const unmatchParenthesesChecker = new CheckFactory().build('unmatchedParentheses')
      const unmatchResults = unmatchParenthesesChecker.check(query)
      // Stop here if the brackets are not matched, and simply return the error array
      if (unmatchResults.length) {
        return unmatchResults
      }
      // Do the quotes check (do this before tokenization since it is based on raw text)
      const unmatchQuotesChecker = new CheckFactory().build('unmatchedQuotes')
      messageArray.push(...unmatchQuotesChecker.check(query))
      // Start parsing the query from the very beginning
      // The messageArray is passed down to update the error messasge on the fly
      query.parseQuery(this.omittedErrors, messageArray)
      if (messageArray.length > 0)
        this.messageMatrix.push(messageArray)
    }
    return this.messageMatrix.flat()
  }

  /** 
   * Function used to extract all table names referenced in the query
   * @return {Object}
   */ 
  getReferences () {
    if (!allTablesColumns.tables.length) {
      this.lintCheck()
    }
    return allTablesColumns
  }

  /**
   * Return the lint error/warning messages
   */
  getLintMessages () {
    return this.messageMatrix.flat()
  }

  /**
   * Return the tokenization result for the query
   */
  getTokenResults () {
    return queryTokens
  }
}

/**
 * Class: Line
 * Contains raw query text, and the corresponding line number in the query
 */
class Line {
  constructor(content, lineNum) {
    this.content = content
    this.lineNum = lineNum
  }
}

/**
 * Class: Query
 * Regard to single query (deliminated by ';')
 * Contains raw text information in array `lines`
 */
class Query {
  constructor() {
    this.lines = []
  }
  getContent() {
    return this.lines.reduce((accumulator, currentValue) => `${accumulator}${currentValue.content} `, '')
  }

  /**
   * Method used to parse the Query object
   * Catch the nested structure and feed the subquery to Checkers on the fly
   * @param {Object} query Query instance that is read and constructed from lines
   * @param {Array} omittedErrors 1D array of checker rules to skip
   * @param {Array} messageArray 1D array storing CheckReuslt instances
   */
  parseQuery(omittedErrors, messageArray) {
    subqueryId = 0
    subqueryReturnedColumns = {} // Re-init this for each single query on the page
    const statement = Object.keys(STATEMENT).map(s => s.toLowerCase())
    const currTokenStack = [] // Stack storing the subqueryToken
    let currString = ''
    let currLine = 0 // Current line number being processed
    let currOffset = 0
    let singleQuoteOpen = false
    this.lines.forEach(line => {
      // Clear the tokens of last line
      if(currString.length > 0 && !singleQuoteOpen) {
        const offset = { start: currOffset, end: currOffset + currString.length }
        currOffset += currString.length
        currTokenStack.push(new Token(currString, currLine, offset))
        currString = ''
      }
      // Start the new line
      currOffset = 0
      let charIndex = 0
      let text = line.content
      currLine = line.lineNum
      // Read the text line by character
      while (charIndex < text.length) {
        // switch the single quote status
        if (text[charIndex] === SEPARATOR.SingleQuote) {
          singleQuoteOpen = !singleQuoteOpen
          currOffset++
          // Push back the Token with type Quoted_String, useful when applying invalidColumnName checker
          // Also assuming there is no other char before the starting '
          if (!singleQuoteOpen && currString) {
            const offset = { start: currOffset, end: currOffset + currString.length + 1}
            currTokenStack.push(new Token(currString, currLine, offset, TOKEN_TYPES.Quoted_String))
            currOffset = currOffset + currString.length + 1
            currString = ''
          }
        }
        // Deal with comma, treat it as a single token
        else if (text[charIndex] === SEPARATOR.Comma) {
          if (currString !== '' && !singleQuoteOpen) {
            const offset = { start: currOffset, end: currOffset + currString.length }
            currTokenStack.push(new Token(currString, currLine, offset))
            currOffset += currString.length
            currString = ''
          }
          const offset = { start: currOffset, end: currOffset + 1}
          currTokenStack.push(new Token(SEPARATOR.Comma, currLine, offset, TOKEN_TYPES.Separator))
          currOffset += 1
        }
        // Deal with space in the middle, usually is a signal to push a token
        else if (text[charIndex] === ' ') {
          if (currString === '') { // Avoid multiple spaces in between
            currOffset++
          }
          else if (!singleQuoteOpen) {
            const offset = { start: currOffset, end: currOffset + currString.length }
            currTokenStack.push(new Token(currString, currLine, offset))
            currOffset += currString.length + 1
            currString = ''
          } else {
            currOffset++
            currString += ' '
          }
        } 
        // Keep rolling for the funcChunk if it cross several lines
        else if (text[charIndex] === SEPARATOR.LeftPar) {
          // Push in the new leftPar
          if (currString.length) { // Has prefix string, usually a function name
            const offset = { start: currOffset, end: currOffset + currString.length }
            currTokenStack.push(new Token(currString, currLine, offset, TOKEN_TYPES.Function_Name))
            currOffset += currString.length
            currString = ''
          }
          const offset = { start: currOffset, end: currOffset + 1 }
          currTokenStack.push(new Token(SEPARATOR.LeftPar, currLine, offset))
          currOffset += 1
        } else if (text[charIndex] === SEPARATOR.RightPar) {
          // First push out the last part of the line
          if (currString.length) {
            const offset = { start: currOffset, end: currOffset + currString.length }
            currTokenStack.push(new Token(currString, currLine, offset))
            currOffset += currString.length
            currString = ''
          }
          /**
           * Check if this is the end of a subquery or function body
           * Ignore the function body and deal with the subquery
           * Put back abstract symbol with either 'subqueryChunk' or 'function_chunk_funName' to parent query
           * Wasys to identify:
           * -> the chunk starts with a statement keyword: subquery
           * -> otherwise
           *   -> the chunk's previous token ends with char: function chunk with function name
           *   -> otherwise, previous token ends with non-char: function chunk without function name
           */
          const potentialSubquery = new Subquery(this._popSubquery(currTokenStack))
          const tokenStackLen = potentialSubquery.tokenStack.length
          // Set the offset of the bracket guarded parts (3 senarios) as the offset of the right bracket
          const offset = { start: currOffset, end: currOffset + 1}
          if (tokenStackLen && statement.includes(potentialSubquery.tokenStack[tokenStackLen - 1].value)) {
            potentialSubquery.checkersAssign(omittedErrors, messageArray)
            // Update the subqueryReturnedColumns
            subqueryReturnedColumns[subqueryId] = Object.values(subquerySelectedColumns)
            currTokenStack.push(new Token(`${subqueryChunk}_${subqueryId}`, currLine, offset))
            subqueryId++
          }
          else if (currTokenStack.length && currTokenStack[currTokenStack.length - 1].type === TOKEN_TYPES.Function_Name) {
            const func_name = currTokenStack.pop().value
            currTokenStack.push(new Token(`${functionChunk}_${func_name}`, currLine, offset))
          }
          else {
            // Guess there's no anonymous function in snowflake, 
            // The potentialSubquery guarded by brackts here are treated as an array of tokens
            // currTokenStack.push(new Token(`${functionChunk}_anonymous`, currLine))
            if (Object.keys(OPERATOR).includes(currTokenStack[currTokenStack.length - 1].value)) {
              currTokenStack.push(new Token(potentialSubquery.getContent(), currLine, offset))
            } else {
              currTokenStack.push(...potentialSubquery.tokenStack.reverse())
            }
          }
        } else {
          // important: transfer all value of our token to lowercase
          currString += text[charIndex].toLowerCase()
        }
        charIndex++
      }
    })
    // Deal with the last token in the last line of text
    if (currString.length) {
      const offset = { start: currOffset, end: currOffset + currString.length }
      currOffset += currString.length
      currTokenStack.push(new Token(currString, currLine, offset))
    }
    // Deal with the most exterior query
    if (currTokenStack.length) {
      const subquery = new Subquery(currTokenStack.splice(0, currTokenStack.length).reverse())
      subquery.checkersAssign(omittedErrors, messageArray)
    }
  }

  /**
 * Helper Function used to pop out the subquery tokens untill a '(' is reached
 * @param {Array} currTokenStack Array containing ALL tokens parsed till now
 */
  _popSubquery(currTokenStack) {
    const subqueryStack = []
    while (currTokenStack.length && currTokenStack[currTokenStack.length - 1].value != SEPARATOR.LeftPar) {
      subqueryStack.push(currTokenStack.pop())
    }
    // Pop out '('
    if (currTokenStack.length && currTokenStack[[currTokenStack.length - 1]].value === SEPARATOR.LeftPar) {
      currTokenStack.pop()
    }
    return subqueryStack
  }
}

/**
 * Class: Subquery (same scope as an `Select` instance)
 * The subquery instance is extracted from a recursion calls for query
 * It contains only one level query, and has no nested structer
 * - tokenStack: An array of Tokens, generated when Query was parsed by parseQuery(), 
 * The tokens initially only contains information of {value, lineNum, offset} and will be filled with more in the tokenization stage
 * - statement: the statement/conmmand the subquery map to (Select in this case)
 */
class Subquery {
  constructor(subqueryStack) {
    this.tokenStack = subqueryStack
    this.statement = null
  }
  getContent() {
    return Array.from(this.tokenStack).reduceRight((accumulator, currentValue) => `${accumulator}${currentValue.value} `, '')
  }

  /**
   * Method used to catch the category of subquery (eg. SELECT) based on the leading word in the content
   * @param {Object} content content of the subquery
   */
  categorise(content) {
    content = content.trim().toLowerCase()
    const result = Object.keys(KEYWORD).find(keyword => content.startsWith(KEYWORD[keyword]))
    if (result) {
      return KEYWORD[result]
    }
    console.error(`Unable to categorise query: ${content}. The query must start with one of ${Object.keys(KEYWORD)}`)
  }

  /**
   * Method used to tokenise the query object
   * Based on statement category, construct the statement instance and call its tokenise method 
   * The change of filling the subquery is made in place, no need to return the updated subquery
   * Note: subquery passed in this function MUST already be filtered to be `SELECT` statement
   * @param {Object} subquery The Subquery instance representing single layer query
   */
  tokenise() {
    this.statement = new Select()
    this.statement.tokenise(this)
    this.statement.getSubqueryTables(this)
    this.statement.getSubqueryColumns(this)
    this.statement.replaceChunk(this)
    this.statement.catchLeftOverColumns(this)
    // Here the tokenization for subquery is done, summarize the information for general usage
    allTablesColumns.tables = allTablesColumns.tables.concat(Object.keys(subqueryTables).map(alias => aliasMap[alias]).filter(table => !_.isNil(table) && !table.startsWith('subquery_chunk')))
    allTablesColumns.columns = allTablesColumns.columns.concat(Object.keys(subquerySelectedColumns))
    // Append tokenStack to global token list
    queryTokens.push(this.tokenStack)
  }

  /**
   * Method used to assign the query to related checkers
   * Find out if the query statment is not supported
   * @param {Array} omittedErrors 1D array of checker rules to skip
   * @param {Array} messageArray 1D array storing CheckReuslt instances
   */
  checkersAssign (omittedErrors, messageArray) {
    const content = this.getContent().trim()
    if (content) {
      const category = this.categorise(content)
      /*
      * If a query belongs to a statement other than 'SELECT', send back an notification
      * Then return and end the lint process
      */
      if (Object.values(STATEMENT_UNSUPPORTED).includes(category)) {
        messageArray.push(new CheckerResult('This kind of query is not supported here, only SELECT is supported.'))
        return
      }
      // Now only support select statement
      if (category !== STATEMENT.Select) {
        return
      }
      /* If this is a SELECT statemnt, the query will be passed on to be tokenised
      * Token Type is filled in place
      */
      this.tokenise()
      // The CheckFactory will then navigate us to loop over the relative checkers
      const factory = new CheckFactory()
      /* The tokenised subquery will then go through each of the checkers 
      * and print out the error message into the console
      * and return the "messsageArray" which stores the CheckResult
      */
      CHECK_RULE.forEach(check => {
        // Skip the parentheses check since it is already done
        // Skip the rule when it is in omittedErrors
        if (omittedErrors.includes(check) || ['unmatchedParentheses', 'unmatchedQuotes'].includes(check)) {
          return
        }
        const checker = factory.build(check)
        if (checker.appliesTo.includes(category) &&
          !checker.requiresConnection) {
          this.check(checker, messageArray)
        }
      })
    }
  }

  /**
   * Function used to apply the checker on given subqueries
   * Update the checking result to messageArray
   * @param {Object} checker Checker Rule instance
   * @param {Object} subquery The Subquery instance representing single layer query
   * @param {Array} messageArray 1D array storing CheckReuslt instances
   */
  check(checker, messageArray) {
    if (typeof checker === 'undefined') {
      return
    }
    const results = checker.check(this)
    messageArray.push(...results)
  }
}

/**
 * Class: Token
 * The basic class for Token in Subquery
 * Contains 
 */
class Token {
  constructor(value, line, offset = {}, type = null) {
    this.value = value
    this.line = line
    this.offset = offset
    this.type = type
  }
}

class TokenAlias extends Token {
  constructor (value, line, offset, type, entity = null, entityName = null) {
    super(value, line, offset, type)
    this.entity = entity // TOKEN_TYPES.TableName or TOKEN_TYPES.ColumnName
    this.entityName = entityName
  }
}

class TokenOperator extends Token {
  constructor (value, line, offset, type, operatorType = 'binary', negation = false, left = null, right = null) {
    super(value, line, offset, type)
    this.operatorType = operatorType
    this.left = left
    this.right = right
    this.negation = negation
  }
}

/**
 * ****************************
 * Util Classes - Checker
 * - CheckResult: class used to construct single check result
 * - Check: The base class for the Check Rule Classes
 * - CheckFactory: class used to initiate Checker instance (e.g. UnmatchedPartheses)
 * ****************************
 */
class CheckerResult {
  constructor(content, tokenValue = '', line = 1, offset = {start: 1, end: 1}, type = 'general', ruleName = 'general') {
    this.line = line
    this.content = content
    this.tokenValue = tokenValue
    this.offset = offset
    this.messageType = type
    this.ruleName = ruleName
  }
}

class Check {
  constructor() {}
  // Infers the name of the error message from the child's class name.
  getName() {
    return this.ruleName
  }

  updateAssertionState (results) {
    if (results.length) {
      assertionState[this.getName()] = false
    } else {
      assertionState[this.getName()] = true
    }
  }

  dependentRulesPassed () {
    for (const rule of this.dependentRules) {
      if (assertionState.hasOwnProperty(rule) && !assertionState[rule]) {
        return false
      }
    }
    return true
  }
}

class CheckFactory {
  build(check) {
    const checkMap = {
      'unmatchedParentheses': new UnmatchedParentheses(),
      'unmatchedQuotes': new UnmatchedQuotes(),
      'missingColumnName': new MissingColumnName(),
      'missingTableName': new MissingTableName(),
      'missingAliasName': new MissingAliasName(),
      'missingColSeparator': new MissingColSeparator(),
      'missingExpression': new MissingExpression(),
      'missingOrderByEntities': new MissingOrderByEntities(),
      'missingGroupByEntities': new MissingGroupByEntities(),
      'missingLimitNumber': new MissingLimitNumber(),
      'missingAliasForSubquery': new MissingAliasForSubquery(),
      'invalidTableName': new InvalidTableName(),
      'invalidColumnName': new InvalidColumnName(),
      'invalidCondition': new InvalidCondition()
    }
    return checkMap[check]
  }
}

/**
 * RULE: UnmatchedParentheses
 * UnmatchedParentheses should be checked first of all.
 * Otherwise the recursion won't work
 */
class UnmatchedParentheses extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'UnmatchedParentheses'
    this.message = 'Unmatched parentheses.'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = []
  }
  check(query) {
    const results = []
    const leftParPosStack = []
    let leftParCount = 0
    query.lines.forEach(line => {
      if (leftParCount < 0) {
        return
      }
      const content = line.content
      const lineNumber = line.lineNum
      for(let charIdx = 0; charIdx < content.length; charIdx++) {
        if (content[charIdx] === SEPARATOR.LeftPar) {
          leftParCount++
          leftParPosStack.push({line: lineNumber, colNum: charIdx})
        } else if (content[charIdx] === SEPARATOR.RightPar) {
          leftParCount--
          if (leftParCount < 0) {
            const offset = { start: charIdx, end: charIdx + 1 }
            results.push(new CheckerResult(`${this.type}: ${this.message}`, SEPARATOR.RightPar, lineNumber, offset, this.type, this.ruleName))
            break
          }
          leftParPosStack.pop()
        }
      }
    })
    if (leftParCount > 0) {
      const leftParPos = leftParPosStack.pop()
      const offset = { start: leftParPos.colNum, end: leftParPos.colNum + 1 }
      results.push(new CheckerResult(`${this.type}: ${this.message}`, SEPARATOR.LeftPar, leftParPos.line, offset, this.type, this.ruleName))
    }
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Unmatched Single Quotes
 * The rule checks if the single quotes in the query match
 * The error will be emited if the quotes doesn't match
 */
class UnmatchedQuotes extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'UnmatchedQuotes'
    this.message = 'Unmatched Quotes.'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(query) {
    const results = []
    const lastQuote = []
    let quoteCount = 0
    query.lines.forEach(line => {
      if (quoteCount < 0) {
        return
      }
      const content = line.content
      const lineNumber = line.lineNum
      for(let charIdx = 0; charIdx < content.length; charIdx++) {
        if (content[charIdx] === '\'') {
          if (quoteCount > 0) {
            quoteCount--
            lastQuote.pop()
          } else {
            quoteCount++
            lastQuote.push({line: lineNumber, colNum: charIdx})
          }
        }
     }
    })
    if (quoteCount > 0) {
      const quote = lastQuote.pop()
      const offset = { start: quote.colNum, end: quote.colNum + 1 }
      results.push(new CheckerResult(`${this.type}: ${this.message}`, '\'', quote.line, offset, this.type, this.ruleName))
    }
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Missing Column Name
 * This rule checks if there are column names missing in the query
 * This error is emitted when there are ',' or 'from' keyword directly following anthoer ',' or 'select'
 */
class MissingColumnName extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingColumnName'
    this.message = 'a column name might be missing'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if ([KEYWORD.Select, SEPARATOR.Comma, KEYWORD.Distinct].includes(token.value)) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`, token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (lastToken !== null && [SEPARATOR.Comma, KEYWORD.Select, KEYWORD.Distinct].includes(lastToken.value) 
        && ![TOKEN_TYPES.ColumnName, TOKEN_TYPES.Subquery_Chunk, TOKEN_TYPES.Function_Chunk].includes(token.type)
        && KEYWORD.Case !== token.value) {
          // Skip the case when Distinct is following Select/Comma
          if (token.value === KEYWORD.Distinct && [SEPARATOR.Comma, KEYWORD.Select].includes(lastToken.value)) {
            lastToken = token
            return
          }
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Missing Table Name
 * This rule checks if there are table names missing in the query
 * The error is emitted when a keyword follows either 'from' or 'join' directly
 */
class MissingTableName extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingTableName'
    this.message = 'a table name might be missing'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if ([KEYWORD.From, KEYWORD.Join].includes(token.value)) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`, token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (lastToken !== null && [KEYWORD.From, KEYWORD.Join].includes(lastToken.value) && ![TOKEN_TYPES.TableName, TOKEN_TYPES.Subquery_Chunk].includes(token.type)) {
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Missing Alias Name
 * This rule checks if there is alias name missing (after keyword 'as')
 * This error is emiteed when keyword 'as' is followed by another keyword
 */
class MissingAliasName extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingAliasName'
    this.message = 'an alias name is missing'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if ([KEYWORD.As].includes(token.value)) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`, token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (lastToken !== null && lastToken.value === KEYWORD.As && token.type !== TOKEN_TYPES.Alias) {
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Missing Separator
 * This rule checks if there are commas missing, which means placing two column name consecutively
 * This error is emitted when an predefined 'Alias' is actually a column name in the format of tablename.colname -> contains '.' (this is an assumption)
 * or a string is following an alias
 */
class MissingColSeparator extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingColSeparator'
    this.message = 'a \',\' or a keyword is expected but another reference name found'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    for (const token of subqueryTokenised.tokenStack) {
      // Missing comma, keyword, and is not a alias name (alias name don't include '.')
      if (lastToken !== null && [TOKEN_TYPES.ColumnName, TOKEN_TYPES.Alias].includes(lastToken.type) && token.type === TOKEN_TYPES.ColumnName) {
        results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
      }
      lastToken = token
    }
    this.updateAssertionState(results)
    return results
  }
 }

/**
 * RULE: Missing Expression
 * This rule checks if a condition expression is missed
 * The error is emitted when a keyword follows 'on'/'where'/'having' directly instead of an expression 
 */
class MissingExpression extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingExpression'
    this.message = 'an expected condition expression is missing'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if ([KEYWORD.Where, KEYWORD.Having, KEYWORD.On].includes(token.value)) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`,token.value, token.line, token.offset, this.type, this.ruleName))
        }
      }
      else {
        if (lastToken !== null && [KEYWORD.On, KEYWORD.Where, KEYWORD.Having].includes(lastToken.value) && token.type !== TOKEN_TYPES.Operator_Left) {
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    }) 
    this.updateAssertionState(results)
    return results
  }
}

 /**
  * RULE: Missing Order By Entities
  * This rule checks if an 'order by' column/columnList is missing
  * The error is emmited if the token following order by doesn't have a type of ColumnName
*/
class MissingOrderByEntities extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingOrderByEntities'
    this.message = 'a column name or column list is exptected following the \'order by\' keyword'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if (token.value === 'order by') {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`,token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (lastToken !== null && lastToken.value === 'order by' && token.type !== TOKEN_TYPES.ColumnName) {
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

 /**
  * RULE: Missing Group By Entities
  * This rule checks if an 'order by' column/columnList is missing
  * The error is emmited if the token following order by doesn't have a type of ColumnName
  */
class MissingGroupByEntities extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingGroupByEntities'
    this.message = 'a column name or column list is exptected following the \'group by\' keyword'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if (token.value === 'group by') {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`,token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (lastToken !== null && (lastToken.value === 'group by') && token.type !== TOKEN_TYPES.ColumnName) {
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
* RULE: Missing Limit Number
* This rule checks if a number restriction following 'limit' is missing
* The error is emmited if current token has value 'limit', but it is the end of the query or the next token doesn't have a type of 'number'
*/
class MissingLimitNumber extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingLimitNumber'
    this.message = 'the number of returned rows is missing (following the \'limit\' keyword)'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    const results = []
    subqueryTokenised.tokenStack.forEach((token,idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if (token.value === KEYWORD.Limit) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`,token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (token.value === KEYWORD.Limit && subqueryTokenised.tokenStack[idx + 1].type !== TOKEN_TYPES.Number) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`, token.value, token.line, token.offset, this.type, this.ruleName))
        }
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Missing Alias for Subquery
 * This rule checks if an alias name is given for a subquery (it is always suggested/enforced to have that)
 * The warning is emitted if the token following a subqueryChunk is either not keyword "AS" nor token type "Alias"
 */
class MissingAliasForSubquery extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'MissingAliasForSubquery'
    this.message = 'an alias name is recommended for a subquery'
    this.type = MESSAGE_TYPE.WARNING
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1) {
        if ([TOKEN_TYPES.Subquery_Chunk].includes(token.type)) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`,token.value, token.line, token.offset, this.type, this.ruleName))
        }
      } else {
        if (lastToken !== null && lastToken.type === TOKEN_TYPES.Subquery_Chunk  && token.type !== TOKEN_TYPES.Alias && token.value !== KEYWORD.As) {
          results.push(new CheckerResult(`${this.type} at line ${lastToken.line}: ${this.message}`, lastToken.value, lastToken.line, lastToken.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Invalid Table Name
 * This rule checks if a table reference is valid or not
 * The error is emitted if any token with type 'table_name' has a value that does not exist in our data catelog
 */
class InvalidTableName extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'InvalidTableName'
    this.message = ['the table with that name does not exist', 'the table name is not in proper format. It is recommended to be like "databaseName.schemaName.tableName"']
    this.type = [MESSAGE_TYPE.ERROR, MESSAGE_TYPE.WARNING]
    this.typeIdx = 1 // Default being warning
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    if (_.isEmpty(snowflakeTablesTree)) {
      return []
    }
    const results = []
    subqueryTokenised.tokenStack.forEach(token => {
      if (token.type === TOKEN_TYPES.TableName && !token.value.startsWith(subqueryChunk)) {
        const tablePath = token.value.trim().split('.')
        if (tablePath.length === 1 && tablePath[0].endsWith('table')) {
          return
        }
        else if (tablePath.length < 3) {
          this.typeIdx = 1
          results.push(new CheckerResult(`${this.type[this.typeIdx]} at line ${token.line}: ${this.message[this.typeIdx]}`, token.value, token.line, token.offset, this.type[this.typeIdx], this.ruleName))
        } else if (!_.has(snowflakeTablesTree, token.value.trim())) {
          this.typeIdx = 0
          results.push(new CheckerResult(`${this.type[this.typeIdx]} at line ${token.line}: ${this.message[this.typeIdx]}`, token.value, token.line, token.offset, this.type[this.typeIdx], this.ruleName))
        }
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Invalid Column Name
 * This rule checks if a column reference is valid or not
 * The error is emitted if any token with type 'column_name' has a value that does not exist in correlated tables
 * This rule is checked AFTER checking the invalidTableName, where the tables refered is extracted from the query
 */
class InvalidColumnName extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'InvalidColumnName'
    this.message = ['the column with that name does not exist', 'invalid column name format']
    this.type = [MESSAGE_TYPE.ERROR, MESSAGE_TYPE.WARNING]
    this.typeIdx = 1 // Default being warning
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.prefix = `[sql-lint: ${this.getName()}] `
    this.dependentRules = ['UnmatchedParentheses', 'InvalidTableName']
  }
  check(subqueryTokenised) {
    // Check for dependency first, this rule require `InvalidTableName` to be passed first
    if (!this.dependentRulesPassed()) {
      return []
    }
    const results = []
    subqueryTokenised.tokenStack.forEach(token => {
      if (token.type === TOKEN_TYPES.ColumnName && !token.value.startsWith(functionChunk) && !token.value.startsWith(TOKEN_TYPES.Quoted_String) && !parseInt(token.value)) {
        const columnPath = token.value.trim().split('.')
        if (columnPath.length === 1) { // columnName with no prefix, check all query tables and aliasMap
          if (token.value !== KEYWORD.AllColumn && !Object.values(subqueryTables).flat().includes(token.value) && !Object.keys(aliasMap).includes(token.value)) {
            if (parseInt(token.value) !== null && parseInt(token.value) <= Object.keys(subquerySelectedColumns).length) {
              return
            }
            this.typeIdx = 0
            results.push(new CheckerResult(`${this.type[this.typeIdx]} at line ${token.line}: ${this.message[this.typeIdx]}`, token.value, token.line, token.offset, this.type[this.typeIdx], this.ruleName))
          }
        } else if (columnPath.length === 2) { // columnname with prefix, check corresponding query table
          if (!subqueryTables.hasOwnProperty(columnPath[0]) || !subqueryTables[columnPath[0]].includes(columnPath[1])) {
            this.typeIdx = 0
            results.push(new CheckerResult(`${this.type[this.typeIdx]} at line ${token.line}: ${this.message[this.typeIdx]}`, token.value, token.line, token.offset, this.type[this.typeIdx], this.ruleName))
          }
        } else {
            this.typeIdx = 1
            results.push(new CheckerResult(`${this.type[this.typeIdx]} at line ${token.line}: ${this.message[this.typeIdx]}`, token.value, token.line, token.offset, this.type[this.typeIdx], this.ruleName))
        }
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

/**
 * RULE: Invalid Condition
 * This rule check is the condition following Where/Having/On is complete or not
 * The error is emitted whenever the left or right part of the operator is missing
 */
class InvalidCondition extends Check {
  constructor() {
    super(...arguments)
    this.ruleName = 'InvalidCondition'
    this.message = 'the condition is invalid'
    this.type = MESSAGE_TYPE.ERROR
    this.requiresConnection = false
    this.appliesTo = ['select']
    this.dependentRules = ['UnmatchedParentheses']
  }
  check(subqueryTokenised) {
    if (!this.dependentRulesPassed()) {
      return []
    }
    let lastToken = null
    const results = []
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (idx === subqueryTokenised.tokenStack.length - 1 && token.type === TOKEN_TYPES.Operator) {
        results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`, token.value, token.line, token.offset, this.type, this.ruleName))
      }
      else {
        if (lastToken !== null && token.type === TOKEN_TYPES.Operator && (lastToken.type !== TOKEN_TYPES.Operator_Left || subqueryTokenised.tokenStack[idx + 1].type !== TOKEN_TYPES.Operator_Right)) {
          results.push(new CheckerResult(`${this.type} at line ${token.line}: ${this.message}`, token.value, token.line, token.offset, this.type, this.ruleName))
        }
        lastToken = token
      }
    })
    this.updateAssertionState(results)
    return results
  }
}

 /**
 * ****************************
 * Statement Class 
 * - options: Variable define the options that DIRECTLY following the statement (eg. table)
 * - tokenise: Method used to tokenize the query to specify the token types, all text here is transformed into lowercase
 * < Select >
 * SELECT is mostly often used statement on our query_pad, the only supported statement now
 * This class has a more detailed tokenizer (can capture more token types)
 * ****************************
 */
class Select {
  constructor() {
    this.options = []
    // Always re-initialized whenever a subquery is constructed and validated
    aliasMap = {}
    subqueryTables = {}
    subquerySelectedColumns = {}
    assertionState = {}
  }
  /**
   * Toknization method for SELECT query
   * @param {Object} subquery The Subquery instance representing single layer query
   */
  tokenise(subquery) {
    // Used the value of the Keyword object (all lowercased string && symbol like ::) 
    const keywords = Object.values(KEYWORD)
    const operators = Object.keys(OPERATOR)
    const concats = Object.values(CONCAT)
    // For paired keywords, need to use the lowercased verstion of the KEYs
    const keywordsPaired = Object.keys(KEYWORD_PAIRED).map(keyword => keyword.toLowerCase())
    let lastToken = null
    let index = subquery.tokenStack.length - 1
    let negat = false
    while (index >= 0) {
      // Value of currentToken
      const item = subquery.tokenStack[index].value
      // Deal with special split() case that causes empty str: 'day,'
      if (item === '') {
        index--
        continue
      }
      // Deal with Single Keywords
      else if (keywords.includes(item) && item !== KEYWORD.AllColumn) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Keyword
      }
      // Deal with Paired Keyword, like "left join, group by..etc", assume not containing Comma in the second part
      else if (keywordsPaired.includes(item) && index >= 1 && (`${item} ${subquery.tokenStack[index - 1].value}`) === KEYWORD_PAIRED[item]) {
        subquery.tokenStack[index].value = KEYWORD_PAIRED[item]
        subquery.tokenStack[index].type = TOKEN_TYPES.Keyword
        subquery.tokenStack[index].offset.end = subquery.tokenStack[index].offset.start + KEYWORD_PAIRED[item].length
        subquery.tokenStack.splice(index - 1, 1)
        index--
      }
      // Deal with Comma
      else if (item === SEPARATOR.Comma) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Separator
      }
      // Deal with concatenation (in order to capture condition statement, removed and/or from keyword)
      else if (concats.includes(item)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Concat
      }
      // Deal with Operator, only handle left part
      else if (operators.includes(item)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Operator
        // Deal with negation
        if (OPERATOR[item] === 'negation') {
          if (lastToken.type === TOKEN_TYPES.Operator) { // Post-negat
            lastToken.negation = true
          }
          else if (index >= 1) { // Pre-negat
            if (operators.includes(subquery.tokenStack[index - 1].value)) {
              negat = true
            }
          }
          subquery.tokenStack.splice(index, 1)
        } else {
          const oldToken = subquery.tokenStack[index]
          subquery.tokenStack[index] = new TokenOperator(oldToken.value, oldToken.line, oldToken.offset, oldToken.type, OPERATOR[item], negat)
          negat = false
          if ([TOKEN_TYPES.Unidentified, TOKEN_TYPES.DateAndTime].includes(lastToken.type)) {
            lastToken.type = TOKEN_TYPES.Operator_Left
            subquery.tokenStack[index].left = lastToken.value
          }
        }
      }
      // Deal with Operator, handle operator_right
      else if (lastToken.type === TOKEN_TYPES.Operator) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Operator_Right
        lastToken.right = lastToken.operatorType === 'binary' ? subquery.tokenStack[index].value : {left: item, right: null, concat: null}
      }
      // Keep dealing with right part, roll back from trinocular.right.right
      else if (index + 4 <= subquery.tokenStack.length && subquery.tokenStack[index + 3].type === TOKEN_TYPES.Operator && subquery.tokenStack[index + 3].operatorType === 'trinocular'
      && [subquery.tokenStack[index + 3].right.concat, subquery.tokenStack[index + 3].right.right].every(elem => elem === null)) {
        const tokenOperator = subquery.tokenStack[index + 3]
        const tokenOperatorRight = subquery.tokenStack[index + 2]
        const tokenOperatorRightRight = subquery.tokenStack[index]
        const tokenConcat = lastToken
        tokenOperator.right.concat = tokenConcat.value.slice(0)
        tokenOperator.right.right = tokenOperatorRightRight.value.slice(0)
        tokenOperator.right.left = tokenOperatorRight.value.slice(0)

        tokenOperatorRight.value += ` ${tokenConcat.value} ${tokenOperatorRightRight.value}`
        tokenOperatorRight.offset.end = tokenOperatorRightRight.offset.end

        subquery.tokenStack.splice(index, 2)
      }
      // Deal with Value returned by function
      else if (item.startsWith(functionChunk)) {
        // functionChunk with function name table results in a table
        subquery.tokenStack[index].type = item.endsWith('table') ? TOKEN_TYPES.TableName : TOKEN_TYPES.Function_Chunk
      }
      // Deal with abstract Subquery Symbol subqueryChunk, should be dealt as a Table Reference
      else if (item.startsWith(subqueryChunk)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Subquery_Chunk
      }
      // Deal with Column Reference
      else if ([KEYWORD.Select, KEYWORD.Distinct].includes(lastToken.value) 
      || (lastToken.value === SEPARATOR.Comma && index + 3 <= subquery.tokenStack.length && ([TOKEN_TYPES.ColumnName, TOKEN_TYPES.Alias].includes(subquery.tokenStack[index + 2].type) || [KEYWORD.DESC, KEYWORD.ASC].includes(subquery.tokenStack[index + 2].value)))
      || item === KEYWORD.AllColumn) {
        if (subquery.tokenStack[index].type === TOKEN_TYPES.Quoted_String) {
          subquery.tokenStack[index].value = `${TOKEN_TYPES.Quoted_String}_${subquery.tokenStack[index].value}`
        }
        subquery.tokenStack[index].type = TOKEN_TYPES.ColumnName
      }
      // Deal with Table Reference, 'Select From' or 'Join'
      else if (lastToken.value === KEYWORD.From || lastToken.value.endsWith(KEYWORD.Join)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.TableName
      }
      // Deal with Alias following Keyword 'AS'
      else if (lastToken.value === KEYWORD.As && index + 3 <= subquery.tokenStack.length) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Alias
        const oldToken = subquery.tokenStack[index]
        const olderToken = subquery.tokenStack[index + 2]
        // Deal with Case - When - End
        if (olderToken.value === KEYWORD.End) {
          subquery.tokenStack[index].type = TOKEN_TYPES.Alias
        } else {
          subquery.tokenStack[index] = new TokenAlias(oldToken.value, oldToken.line, oldToken.offset, oldToken.type, olderToken.type, olderToken.value)
          aliasMap[oldToken.value] =  olderToken.value
          // functionChunk with alias are columns， subqueryChunk with alias are tables
          if (olderToken.type === TOKEN_TYPES.Function_Chunk) {
            subquery.tokenStack[index].entity = TOKEN_TYPES.ColumnName
          }
        }
      }
      // Deal with Date and Time (after the cast symbol ::)
      else if (lastToken.value === KEYWORD.Cast) {
        subquery.tokenStack[index].type = TOKEN_TYPES.DateAndTime
      }
      // Deal with Paired Conditioning Keyword ended with 'BY': order by, group by
      else if (lastToken.value.endsWith(KEYWORD.By)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.ColumnName
      }
      // Deal with conditioning 'Limit'
      else if (lastToken.value === KEYWORD.Limit) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Number
      }
      // Deal with indexing after function chunk
      else if (item.startsWith('[')) {
        subquery.tokenStack[index + 1].offset.end += item.length
        subquery.tokenStack.splice(index, 1)
      }
      // Deal with Alias without Keyword 'AS'
      else if ([TOKEN_TYPES.TableName, TOKEN_TYPES.ColumnName, TOKEN_TYPES.Subquery_Chunk, TOKEN_TYPES.Function_Chunk].includes(lastToken.type) && !item.includes('.')) {
        subquery.tokenStack[index].type = TOKEN_TYPES.Alias
        const oldToken = subquery.tokenStack[index]
        subquery.tokenStack[index] = new TokenAlias(oldToken.value, oldToken.line, oldToken.offset, oldToken.type, lastToken.type, lastToken.value)
        // Update the alisaMap, used for schema check
        aliasMap[oldToken.value] = lastToken.value
        // functionChunk with alias are columns
        if (lastToken.type === TOKEN_TYPES.Function_Chunk) {
          subquery.tokenStack[index].entity = TOKEN_TYPES.ColumnName
        }
      }
      // Deal with the possible leftover column names due to lack of comma
      else if ([TOKEN_TYPES.ColumnName, TOKEN_TYPES.Alias].includes(lastToken.type)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.ColumnName
      }
      // Deal with non-command predefined variables, eg: null
      else if (Object.values(PREDEFINED_VAR).includes(item)) {
        subquery.tokenStack[index].type = TOKEN_TYPES.PredefinedVar
      }
      // Deal with Uncaught Types
      else {
        subquery.tokenStack[index].type = TOKEN_TYPES.Unidentified
      }
      lastToken = subquery.tokenStack[index]
      index--
    }
    subquery.tokenStack.reverse()
    return subquery
  }

  /**
   * Method used to get all column names of the refered tables in the subquery 
   * @param {Object} subqueryTokenised subquery after tokenization (funciton processing above)
   */
  getSubqueryTables(subqueryTokenised) {
    if (_.isEmpty(snowflakeTablesTree)) {
      return
    }
    let lastTableName = ''
    subqueryTokenised.tokenStack.forEach(token => {
      if (token.type === TOKEN_TYPES.TableName) {
        const tablePath = token.value.trim().split('.')
        // A valid table path
        if (tablePath.length === 3 && _.has(snowflakeTablesTree, token.value.trim())) {
          subqueryTables[token.value] = _.get(snowflakeTablesTree, token.value.trim())
          lastTableName = token.value
        } 
        // To include the data by applying default database "Production_Data"
        else if (tablePath.length === 2) {
          const completeTableName = `production_data.${token.value.trim()}`
          if (_.has(snowflakeTables, completeTableName)) {
            subqueryTables[token.value] = _.get(snowflakeTablesTree, completeTableName)
            lastTableName = token.value
          }
        }
      } else if (token.type === TOKEN_TYPES.Alias) {
        // For tableName alias, replace the original table with the alias as the key
        if (token.entity === TOKEN_TYPES.TableName) {
          subqueryTables[token.value] = subqueryTables[lastTableName]
          delete subqueryTables[token.entityName]
        } 
        // For subquery alias, pass back all the selected columns in the subquery
        else if (token.entity === TOKEN_TYPES.Subquery_Chunk) {
          const subqueryPath = token.entityName.split('_')
          const id = subqueryPath[subqueryPath.length - 1]
          subqueryTables[token.value] = subqueryReturnedColumns[id]
        }
      }
    })
  }

  /**
   * Method used to get all selected column names OR their alias in the subquery
   * Maintained in the values of the global object `subquerySelectedColumns`
   * @param {Object} subqueryTokenised subquery after tokenization
   */
  getSubqueryColumns(subqueryTokenised) {
    let selectFinished = false
    _.forEach(subqueryTokenised.tokenStack, token => {
      if (selectFinished) {
        return false
      }
      if (token.value === KEYWORD.From) {
        selectFinished = true
      }
      if (token.type === TOKEN_TYPES.ColumnName) {
        const columnPath = token.value.split('.')
        // Expand the selected columns if there is the *
        if (token.value === KEYWORD.AllColumn) {
          subquerySelectedColumns[token.value] = Object.values(subqueryTables)
        } else {
          subquerySelectedColumns[token.value] = columnPath.length > 1 ? columnPath[columnPath.length - 1] : token.value
        }
      } else if (token.type === TOKEN_TYPES.Alias && token.entity === TOKEN_TYPES.ColumnName) {
        subquerySelectedColumns[token.entityName] = token.value
      }
    })
  }

  /**
   * Method used to replace some chunks's type back to tableName or columnName
   * 1. functionChunk with alias are columns
   * 2. subqueryChunk with alias are tables
   * 3. subqueryChunk or functionChunk around Operator are parts of condition: Operator_Left or Operator_Right
   * So as to better assis the rule checking process
   * @param {Object} subqueryTokenised 
   */
  replaceChunk(subqueryTokenised) {
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (token.type === TOKEN_TYPES.Alias && idx >= 1) {
        if (subqueryTokenised.tokenStack[idx - 1].value === KEYWORD.As) {
          if (idx < 2) {
            return
          }
          if (subqueryTokenised.tokenStack[idx - 2].type === TOKEN_TYPES.Subquery_Chunk) {
            subqueryTokenised.tokenStack[idx - 2].type = TOKEN_TYPES.TableName
          } else if (subqueryTokenised.tokenStack[idx - 2].type === TOKEN_TYPES.Function_Chunk) {
            subqueryTokenised.tokenStack[idx - 2].type = TOKEN_TYPES.ColumnName
          }
        } else {
          if (subqueryTokenised.tokenStack[idx - 1].type === TOKEN_TYPES.Subquery_Chunk) {
            subqueryTokenised.tokenStack[idx - 1].type = TOKEN_TYPES.TableName
          } else if (subqueryTokenised.tokenStack[idx - 1].type === TOKEN_TYPES.Function_Chunk) {
            subqueryTokenised.tokenStack[idx - 1].type = TOKEN_TYPES.ColumnName
          }
        }
      }
      else if (token.type === TOKEN_TYPES.Operator) {
        if (idx >= 1 && [TOKEN_TYPES.Subquery_Chunk, TOKEN_TYPES.Function_Chunk].includes(subqueryTokenised.tokenStack[idx - 1].type)) {
          subqueryTokenised.tokenStack[idx - 1].type = TOKEN_TYPES.Operator_Left
        } else if (idx < subqueryTokenised.tokenStack.length - 1 
          && [TOKEN_TYPES.Subquery_Chunk, TOKEN_TYPES.Function_Chunk].includes(subqueryTokenised.tokenStack[idx + 1].type)) {
          subqueryTokenised.tokenStack[idx + 1].type = TOKEN_TYPES.Operator_Right
        }
      }
    })
  }

  /**
   * Method used to catch leftover column references
   * Those are initially tokenized as undefined, but are actually column names
   * Don't need to work on tables since table names are all captured following FROM/JOIN
   * @param {Object} subqueryTokenised 
   */
  catchLeftOverColumns (subqueryTokenised) {
    subqueryTokenised.tokenStack.forEach((token, idx) => {
      if (token.type === TOKEN_TYPES.Unidentified && (Object.values(subquerySelectedColumns).includes(token.value) 
      || Object.entries(subqueryTables).forEach(val => `${val[0]}.${val[1]}` === token.value ))) {
        subqueryTokenised.tokenStack[idx].type = TOKEN_TYPES.ColumnName
      }
    })
  }
}

/**
 * ****************************
 * Util Functions
 * - getQueryFromLine
 * - stripComments
 * - prepareSnowflakeTables
 * ****************************
 */
 /**
  * Function used to construct Query object
  * Read in the raw query text, filter out the comment
  * Construct the text into Query object, return an array of Query objects
  * @param {string} contents Raw query text
  */
function getQueryFromLine(contents) {
  // Append every contents string with a newLine, also capture last line without ';' or '\n' 
  contents += SEPARATOR.Newline
  let lineNumber = 1
  const queries = []
  let currentQueryContent = ''
  let query = new Query()
  const skipChars = [
    '',
    ';',
    SEPARATOR.Newline,
    SEPARATOR.WindowsNewline
  ]
  contents = stripComments(contents)
  for (let index = 0; index < contents.length; index++) {
    if (!skipChars.includes(contents[index])) {
      currentQueryContent += contents[index]
    }
    if (contents[index] === SEPARATOR.Newline) {
      if (currentQueryContent.length > 0) {
        query.lines.push(new Line(currentQueryContent, lineNumber))
      }
      currentQueryContent = ''
      lineNumber++
    }
    if (contents[index] === ';') {
      if (currentQueryContent.length > 0) {
        query.lines.push(new Line(currentQueryContent, lineNumber))
      }
      queries.push(query)
      query = new Query()
      currentQueryContent = ''
    }
  }
  // There might still be one last query without ';' to be pushed
  if (query.lines.length) {
    queries.push(query)
  }
  return queries
}

/**
 * Function to filter out comment in the query
 * Filter out (1) any lines that start with a comment; (2) partial lines appended at end
 * Rejoin the lines together as a single string.
 * @param {string} content The raw query text ended with a new line
 */
function stripComments(content) {
  // Split to lines 
  const contentInLines = content.split(SEPARATOR.Newline)
  for (let index = 0; index < contentInLines.length; index++) {
    // Filter out the illegal characters
    contentInLines[index] = contentInLines[index].replace(/([^0-9A-Za-z._,:*$@()<=>/\\+-\[\] \'])+/g, '')
    // Deal with line that is pure comment 
    if (contentInLines[index].startsWith(COMMENT_SIGNAL.CommentDash) ||
      contentInLines[index].startsWith(COMMENT_SIGNAL.CommentMultiLineStart ||
      contentInLines[index].startsWith(COMMENT_SIGNAL.CommentMultiLineEnd))) {
      contentInLines[index] = ''
    }
    // Deal with line that has a mix of query code and comment
    else {
      const wordsInLine = contentInLines[index].split(SEPARATOR.Space)
      for (let j = 0; j < wordsInLine.length; j++) {
        if (wordsInLine[j].startsWith(COMMENT_SIGNAL.CommentDash)) {
          /*
            * Truncate the array wordsInLine to filter out the comment at tail
            * and set the filtered text to the line (return as part of the query)
            */
          contentInLines[index] = wordsInLine.slice(0, j).join(SEPARATOR.Space)
          break
        }
      }
    }
  }
  return contentInLines.join(SEPARATOR.Newline) // May contain multiple empty lines, will apply trim() later
}

/**
 * Util Function used to preparing the schema data
 * Receive the snowflakeTables object, and re-orgaize it into {databse:{schema:{tableName}}} format
 * @param {Object} snowflakeTables
 */
function prepareSnowflakeTables(snowflakeTables) {
  for (let [key, val] of Object.entries(snowflakeTables)) {
    const table_path = key.trim().toLowerCase().split('.')
    if (!snowflakeTablesTree.hasOwnProperty(table_path[0])) {
      snowflakeTablesTree[table_path[0]] = {}
      snowflakeTablesTree[table_path[0]][table_path[1]] = {}
    } else if (!snowflakeTablesTree[table_path[0]].hasOwnProperty(table_path[1])) {
      snowflakeTablesTree[table_path[0]][table_path[1]] = {}
    }
    snowflakeTablesTree[table_path[0]][table_path[1]][table_path[2]] = val.map(val => val.toLowerCase())
  }
}
